import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { Fragment } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { DialogContent, Dialog } from '@material-ui/core';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import { Checkmark, Printer } from '@mummssoftware/icons';
import Paper from '@material-ui/core/Paper';
import { SectionHeader } from '../SectionHeader';
import { ActionIcon } from '../ActionIcon';
const minHeight = 0.5 * window.innerHeight;
const minWidth = 0.5 * window.innerWidth; // this is to get the dialog to open at the top if selected

const useStyles = makeStyles(() => createStyles({
  scrollPaper: {
    alignItems: 'baseline' // default center

  },
  root: {
    minHeight: `${minHeight}px`,
    minWidth: `${minWidth}px`,
    padding: `8px 8px`
  }
})); // create a blur background on opening dialog

/*  
To clarify: this selector selects all elements (*)...
...that are children of the body (body > *)
...and do not have ID loading (*:not(#dialog)).
*/

const GlobalStyle = createGlobalStyle`
  body > *:not(#dialog) {
    filter: blur(1px);
  }
`;
const StyledDialog = styled(Dialog).withConfig({
  displayName: "Dialog__StyledDialog",
  componentId: "sc-1ffjsfq-0"
})(["&&{pointer-events:none;}& .resizable{position:relative;border:3px solid ", ";border-top:none;overflow:hidden;}& .resizable > .react-resizable-handle{position:absolute;width:20px;height:20px;bottom:0;right:0;background:url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+');background-position:bottom right;padding:0 3px 3px 0;background-repeat:no-repeat;background-origin:content-box;box-sizing:border-box;cursor:se-resize;}"], props => props.theme.palette.background.header);
const StyledPaper = styled(Paper).withConfig({
  displayName: "Dialog__StyledPaper",
  componentId: "sc-1ffjsfq-1"
})(["pointer-events:auto;"]);
const StyledDialogContent = styled(DialogContent).withConfig({
  displayName: "Dialog__StyledDialogContent",
  componentId: "sc-1ffjsfq-2"
})(["&&.MuiDialogContent-root{border:3px solid ", ";border-top:none;}"], props => props.theme.palette.background.header);

function PaperComponent(props) {
  return /*#__PURE__*/React.createElement(Draggable, {
    handle: "#draggable-dialog-title",
    cancel: '[class*="MuiDialogContent-root"]'
  }, /*#__PURE__*/React.createElement(StyledPaper, props));
}

const dragStyle = {
  cursor: 'move'
};

const DialogHeader = ({
  handleClick = () => null,
  onPrint,
  style,
  id,
  title
}) => /*#__PURE__*/React.createElement(SectionHeader, {
  title: title,
  style: style,
  id: id,
  nav: /*#__PURE__*/React.createElement(Fragment, null, onPrint && /*#__PURE__*/React.createElement(ActionIcon, {
    "data-testid": "dialog-done",
    Icon: Printer,
    onClick: onPrint
  }), /*#__PURE__*/React.createElement(ActionIcon, {
    "data-testid": "dialog-done",
    Icon: Checkmark,
    onClick: handleClick
  }))
});
/* disableEnforceFocus : the modal will not prevent focus from leaving the modal while open.
Generally this should never be set to true as it makes the modal less accessible to assistive technologies, like screen readers. 
We need it for cert-app and clearscripts signed verbal tab
*/
// TODO Invalid prop children of type array supplied to resizableBox
// top to have the dialog box open on top of page instead of middle default


const DialogWrap = (_ref) => {
  let {
    draggable,
    children,
    top
  } = _ref,
      props = _objectWithoutProperties(_ref, ["draggable", "children", "top"]);

  let Container;
  const classes = useStyles();

  if (draggable) {
    Container = /*#__PURE__*/React.createElement(StyledDialog, _extends({
      id: "dialog",
      PaperComponent: PaperComponent,
      hideBackdrop: props.hideBackdrop,
      maxWidth: false,
      "aria-labelledby": "draggable-dialog-title"
    }, props), /*#__PURE__*/React.createElement(ResizableBox, {
      height: 400,
      width: 600,
      className: "resizable"
    }, children));
  } else {
    Container = /*#__PURE__*/React.createElement(Dialog, _extends({
      id: "dialog",
      classes: top ? {
        scrollPaper: classes.scrollPaper
      } : {},
      "aria-labelledby": "dialog-title",
      disableEnforceFocus: true // Let the user focus on elements outside the dialog

    }, props), children);
  }

  return Container;
};

export const DialogBox = (_ref2) => {
  let {
    draggable,
    title,
    handleClick,
    onPrint,
    children
  } = _ref2,
      props = _objectWithoutProperties(_ref2, ["draggable", "title", "handleClick", "onPrint", "children"]);

  const classes = useStyles();
  return /*#__PURE__*/React.createElement(DialogWrap, _extends({
    id: "dialog",
    draggable: draggable
  }, props), /*#__PURE__*/React.createElement(DialogHeader, {
    id: `${draggable ? 'draggable-' : ''}dialog-title`,
    title: title,
    style: draggable ? dragStyle : undefined,
    handleClick: handleClick,
    onPrint: onPrint
  }), draggable ? /*#__PURE__*/React.createElement(DialogContent, null, children) : /*#__PURE__*/React.createElement(StyledDialogContent, {
    classes: props.minHeight ? {
      root: classes.root
    } : {}
  }, children));
};
export default DialogBox;